import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Block as BlockIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Refresh as RefreshIcon,
  SwapHoriz as SwapHorizIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  getNetworkInvoices,
  listTenantParties,
  linkPeerToTenantParty,
  unlinkPeerFromTenantParty,
  updateTenantPartyPricing,
  blockPeer,
  unblockPeer,
  type MyNetworkInvoice,
  type TenantParty,
  type TenantPartyPage,
} from '@/api/tenantParties';
import { tenantApi, type SearchField } from '@/api/tenant';
import type { PartyType, PartyPricingTier } from '@/schemas';
import type { PeerTenant, PeerTenantPage } from '@/types';
import { brand } from '@/theme/theme';
import { useAuthStore } from '@/stores/authStore';
import { SearchWithFieldSelect } from '@/components/SearchWithFieldSelect';
import { PaginationFooter } from '@/components/PaginationFooter';
import { PartyDetailDialog } from '@/components/PartyDetailDialog';
import { TenantPartyDetailDialog } from '@/components/TenantPartyDetailDialog';
import { ViewButton } from '@/components/ViewButton';

interface LocationState {
  created?: boolean;
}

const MAP_SEARCH_DEBOUNCE_MS = 150;
const MAP_MIN_SEARCH_CHARS = 2;

// Circular letter badge — same convention as Order Inbox's buyer-type badge: R for retailer,
// W for wholesaler/distributor, SR for sales rep (a distinct coral-red so it doesn't get
// confused with the wholesaler purple).
function PartyTypeChip({ type }: { type?: string | null }) {
  if (!type) return null;
  const palette: Record<string, { bg: string; fg: string; letter: string }> = {
    RETAILER:        { bg: '#C17BAF', fg: '#FFFFFF', letter: 'R' },
    WHOLESALER:      { bg: '#7F77DD', fg: '#26215C', letter: 'W' },
    DISTRIBUTOR:     { bg: '#7F77DD', fg: '#26215C', letter: 'W' },
    REPRESENTATIVE:  { bg: '#FF6F61', fg: '#FFFFFF', letter: 'SR' },
    OTHERS:          { bg: '#9CA3AF', fg: '#1F2937', letter: '?' },
  };
  const c = palette[type] ?? palette.OTHERS;
  return (
    <Box
      sx={{
        width: c.letter.length > 1 ? 24 : 20,
        height: 20,
        borderRadius: c.letter.length > 1 ? '10px' : '50%',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: c.bg,
        color: c.fg,
        fontSize: c.letter.length > 1 ? 9 : 10,
        fontWeight: 700,
      }}
    >
      {c.letter}
    </Box>
  );
}

/**
 * Mapping cell — shows the linked TenantParty details (no action buttons;
 * those live in the Actions column), or an autocomplete to pick one.
 * `forceSwap` from the parent triggers the autocomplete even when already linked.
 */
function MapToMyPartyCell({
  peer,
  forceSwap,
  onChanged,
  onSwapDone,
  onError,
}: {
  peer: PeerTenant;
  forceSwap: boolean;
  onChanged: () => void;
  onSwapDone: () => void;
  onError: (msg: string) => void;
}) {
  const linked = peer.linkedTenantParty ?? null;
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<TenantParty[]>([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);

  const showAutocomplete = !linked || forceSwap;

  useEffect(() => {
    if (!showAutocomplete) return;
    const trimmed = input.trim();
    if (trimmed.length < MAP_MIN_SEARCH_CHARS) {
      setOptions([]);
      return;
    }
    const handle = window.setTimeout(async () => {
      setSearching(true);
      try {
        const page = await listTenantParties(trimmed, false, 0, 10);
        setOptions(page.content);
      } catch {
        setOptions([]);
      } finally {
        setSearching(false);
      }
    }, MAP_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [input, showAutocomplete]);

  const handleSelect = async (tp: TenantParty | null) => {
    if (!tp) return;
    setBusy(true);
    try {
      await linkPeerToTenantParty(tp.id, peer.id);
      setInput('');
      setOptions([]);
      onSwapDone();
      onChanged();
    } catch {
      onError('Could not link party. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (!showAutocomplete && linked) {
    const areaCity = [linked.area, linked.city].filter(Boolean).join(', ');
    const gstDl = [
      linked.gstin ? `GST ${linked.gstin}` : null,
      linked.dlNumber ? `DL ${linked.dlNumber}` : null,
    ].filter(Boolean).join(' • ');

    return (
      <Stack spacing={0.25}>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: brand.violet, lineHeight: 1.25 }}
          >
            {linked.name}
          </Typography>
          {linked.partyType && <PartyTypeChip type={linked.partyType} />}
          {linked.partyCode && (
            <Typography variant="caption" sx={{ color: brand.inkSoft, fontSize: 11 }}>
              {linked.partyCode}
            </Typography>
          )}
        </Stack>
        {areaCity && (
          <Typography variant="caption" sx={{ color: brand.inkSoft, fontSize: 11 }}>
            {areaCity}
          </Typography>
        )}
        {gstDl && (
          <Typography variant="caption" sx={{ color: brand.inkSoft, fontSize: 11 }}>
            {gstDl}
          </Typography>
        )}
      </Stack>
    );
  }

  return (
    <Autocomplete<TenantParty, false, false, false>
      size="small"
      fullWidth
      disabled={busy}
      options={options}
      filterOptions={(x) => x}
      loading={searching}
      noOptionsText={
        input.length < MAP_MIN_SEARCH_CHARS
          ? `Type at least ${MAP_MIN_SEARCH_CHARS} characters…`
          : 'No matches'
      }
      getOptionLabel={(o) => `${o.name}${o.partyCode ? ` • ${o.partyCode}` : ''}`}
      onInputChange={(_, value) => setInput(value)}
      onChange={(_, value) => handleSelect(value)}
      onBlur={() => { if (forceSwap) onSwapDone(); }}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Stack spacing={0.25} sx={{ width: '100%' }}>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {option.name}{option.partyCode ? ` • ${option.partyCode}` : ''}
              </Typography>
              {option.partyType && <PartyTypeChip type={option.partyType} />}
            </Stack>
            {(option.gstin || option.area || option.city) && (
              <Typography variant="caption" sx={{ color: brand.inkSoft }}>
                {[
                  option.gstin && `GST ${option.gstin}`,
                  [option.area, option.city].filter(Boolean).join(', '),
                ].filter(Boolean).join(' • ')}
              </Typography>
            )}
          </Stack>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={forceSwap ? 'Pick a different party…' : 'Find & Link to My Network'}
          variant="outlined"
          autoFocus={forceSwap}
        />
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// Billing radio — PTR / PTS for a linked TenantParty
// ---------------------------------------------------------------------------

function BillingCell({
  linked,
  peerType,
  onError,
  onChanged,
}: {
  linked: TenantParty | null;
  peerType: string;
  onError: (msg: string) => void;
  onChanged: () => void;
}) {
  const defaultTier: PartyPricingTier =
    peerType === 'WHOLESALER' ? 'PTS' : 'PTR';
  const [saving, setSaving] = useState(false);
  const [value, setValue] = useState<PartyPricingTier>(
    linked?.defaultPricing ?? defaultTier,
  );

  useEffect(() => {
    setValue(linked?.defaultPricing ?? defaultTier);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linked?.id]);

  if (!linked) {
    return (
      <Typography variant="caption" sx={{ color: brand.inkSoft, fontSize: 11 }}>—</Typography>
    );
  }

  const handleChange = async (newVal: PartyPricingTier) => {
    const prev = value;
    setValue(newVal);
    setSaving(true);
    try {
      await updateTenantPartyPricing(linked.id, newVal);
      onChanged();
    } catch {
      onError('Could not save billing preference.');
      setValue(prev);
    } finally {
      setSaving(false);
    }
  };

  return (
    <RadioGroup
      value={value}
      onChange={(e) => handleChange(e.target.value as PartyPricingTier)}
    >
      <FormControlLabel
        value="PTR"
        control={<Radio size="small" disabled={saving} sx={{ p: 0.25 }} />}
        label={<Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600 }}>PTR</Typography>}
        sx={{ m: 0, gap: 0.5 }}
      />
      <FormControlLabel
        value="PTS"
        control={<Radio size="small" disabled={saving} sx={{ p: 0.25 }} />}
        label={<Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600 }}>PTS</Typography>}
        sx={{ m: 0, gap: 0.5 }}
      />
    </RadioGroup>
  );
}

// ---------------------------------------------------------------------------
// All Parties tab — directory of peer tenants in the same industry.
// ---------------------------------------------------------------------------

interface AllPartiesTabProps {
  data: PeerTenantPage | null;
  loading: boolean;
  searchTerm: string;
  setPage: (n: number) => void;
  setSize: (n: number) => void;
  load: () => void;
  setActionToast: (msg: string | null) => void;
}

function AllPartiesTab({
  data,
  loading,
  searchTerm,
  setPage,
  setSize,
  load,
  setActionToast,
}: AllPartiesTabProps) {
  const [swappingPeerId, setSwappingPeerId] = useState<number | null>(null);
  const [busyPeerId, setBusyPeerId] = useState<number | null>(null);
  const [viewParty, setViewParty] = useState<PeerTenant | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    type: 'block' | 'unblock' | 'unlink' | 'swap';
    peerId: number;
    peerName: string;
    linked?: TenantParty;
  } | null>(null);

  const handleUnlink = async (linked: TenantParty, peerId: number) => {
    setBusyPeerId(peerId);
    try {
      await unlinkPeerFromTenantParty(linked.id);
      load();
    } catch {
      setActionToast('Could not remove link. Please try again.');
    } finally {
      setBusyPeerId(null);
    }
  };

  const handleBlock = async (peerId: number) => {
    setBusyPeerId(peerId);
    try {
      await blockPeer(peerId);
      setActionToast('Party blocked.');
      load();
    } catch {
      setActionToast('Could not block party. Please try again.');
    } finally {
      setBusyPeerId(null);
    }
  };

  const handleUnblock = async (peerId: number) => {
    setBusyPeerId(peerId);
    try {
      await unblockPeer(peerId);
      setActionToast('Party unblocked.');
      load();
    } catch {
      setActionToast('Could not unblock party. Please try again.');
    } finally {
      setBusyPeerId(null);
    }
  };

  const handleConfirm = () => {
    if (!confirmAction) return;
    const { type, peerId, linked } = confirmAction;
    setConfirmAction(null);
    if (type === 'block') handleBlock(peerId);
    else if (type === 'unblock') handleUnblock(peerId);
    else if (type === 'unlink' && linked) handleUnlink(linked, peerId);
    else if (type === 'swap') setSwappingPeerId(peerId);
  };

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data || data.content.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography sx={{ color: brand.inkSoft, mb: 2 }}>
          {searchTerm
            ? `No parties match "${searchTerm}".`
            : 'No other registered businesses found in your industry yet.'}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <TableContainer>
        <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 620 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40, fontWeight: 600 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '28%' }}>Business</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '26%' }}>My Network Link</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 120 }}>Contact</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 90 }}>Billing</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 110 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.content.map((t: PeerTenant, idx) => {
              const linked = t.linkedTenantParty ?? null;
              const busy = busyPeerId === t.id;
              const isBlocked = !!t.blocked;
              const cityLine = [t.city, t.district].filter(Boolean).join(', ');
              const gstDlLine = [
                t.gstin ? `GST ${t.gstin}` : null,
                t.drugLicenseNumber ? `DL ${t.drugLicenseNumber}` : null,
              ].filter(Boolean).join(' • ');

              return (
                <TableRow
                  key={t.id}
                  hover
                  sx={{ '&:nth-of-type(even)': { background: '#FBFBFD' } }}
                >
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    {data.number * data.size + idx + 1}
                  </TableCell>

                  {/* Business Name */}
                  <TableCell sx={{ verticalAlign: 'top', opacity: isBlocked ? 0.6 : 1 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                      <Typography
                        variant="body2"
                        onClick={() => setViewParty(t)}
                        sx={{ fontWeight: 700, color: brand.violet, lineHeight: 1.25, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {t.name}
                      </Typography>
                      {t.tenantType && <PartyTypeChip type={t.tenantType} />}
                      {isBlocked && (
                        <Chip
                          size="small"
                          label="BLOCKED"
                          sx={{
                            height: 18, fontSize: 10, fontWeight: 700,
                            background: '#FEE2E2', color: '#991B1B', borderRadius: 0.5,
                            '& .MuiChip-label': { px: 0.75 },
                          }}
                        />
                      )}
                    </Stack>
                    {cityLine && (
                      <Typography variant="caption" sx={{ color: brand.inkSoft, fontSize: 11, display: 'block' }}>
                        📍 {cityLine}
                      </Typography>
                    )}
                    {gstDlLine && (
                      <Typography variant="caption" sx={{ color: brand.inkSoft, fontSize: 11, display: 'block' }}>
                        {gstDlLine}
                      </Typography>
                    )}
                  </TableCell>

                  {/* My Party Link */}
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <MapToMyPartyCell
                      peer={t}
                      forceSwap={swappingPeerId === t.id}
                      onChanged={load}
                      onSwapDone={() => setSwappingPeerId(null)}
                      onError={(m) => setActionToast(m)}
                    />
                  </TableCell>

                  {/* Contact */}
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    {t.phone ? (
                      <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600 }}>
                        📞 {t.phone}
                      </Typography>
                    ) : (
                      <Typography variant="caption" sx={{ color: brand.inkSoft, fontSize: 11 }}>—</Typography>
                    )}
                  </TableCell>

                  {/* Billing */}
                  <TableCell sx={{ verticalAlign: 'top', opacity: isBlocked ? 0.4 : 1 }}>
                    <BillingCell
                      linked={linked}
                      peerType={t.tenantType}
                      onError={(m) => setActionToast(m)}
                      onChanged={load}
                    />
                  </TableCell>

                  {/* Actions */}
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    {isBlocked ? (
                      <Tooltip title="Unblock">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => setConfirmAction({ type: 'unblock', peerId: t.id, peerName: t.name })}
                            disabled={busy}
                            sx={{ p: 0.5, color: 'success.main' }}
                          >
                            <BlockIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <Stack direction="row" spacing={0.25} flexWrap="wrap">
                        <Tooltip title="Swap link">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => setConfirmAction({ type: 'swap', peerId: t.id, peerName: t.name, linked: linked ?? undefined })}
                              disabled={busy}
                              sx={{ p: 0.5 }}
                            >
                              <SwapHorizIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Remove link">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => linked && setConfirmAction({ type: 'unlink', peerId: t.id, peerName: t.name, linked })}
                              disabled={busy || !linked}
                              sx={{ p: 0.5 }}
                            >
                              <LinkOffIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Block">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => setConfirmAction({ type: 'block', peerId: t.id, peerName: t.name })}
                              disabled={busy}
                              sx={{ p: 0.5, color: 'error.main' }}
                            >
                              <BlockIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <PaginationFooter
        page={data.number}
        totalPages={data.totalPages}
        total={data.totalElements}
        rowsPerPage={data.size}
        rowsPerPageOptions={[10, 25, 50, 100]}
        onPageChange={setPage}
        onRowsPerPageChange={setSize}
        label="parties"
      />

      {/* ── Confirm dialog ── */}
      <Dialog
        open={confirmAction !== null}
        onClose={() => setConfirmAction(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1 }}>
          {confirmAction?.type === 'block' && 'Block Party'}
          {confirmAction?.type === 'unblock' && 'Unblock Party'}
          {confirmAction?.type === 'unlink' && 'Remove Link'}
          {confirmAction?.type === 'swap' && 'Swap Link'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13 }}>
            {confirmAction?.type === 'block' && (
              <>Block <strong>{confirmAction.peerName}</strong>? They will no longer be able to place orders with you.</>
            )}
            {confirmAction?.type === 'unblock' && (
              <>Unblock <strong>{confirmAction.peerName}</strong>? They will be able to place orders again.</>
            )}
            {confirmAction?.type === 'unlink' && (
              <>Remove the link between <strong>{confirmAction.peerName}</strong> and party <strong>{confirmAction.linked?.name}</strong>?</>
            )}
            {confirmAction?.type === 'swap' && (
              <>Change the linked party for <strong>{confirmAction.peerName}</strong>?
              {confirmAction.linked && <> This will replace the current link to <strong>{confirmAction.linked.name}</strong>.</>}</>
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => setConfirmAction(null)} size="small">Cancel</Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            size="small"
            color={confirmAction?.type === 'unblock' ? 'success' : 'error'}
          >
            {confirmAction?.type === 'block' && 'Block'}
            {confirmAction?.type === 'unblock' && 'Unblock'}
            {confirmAction?.type === 'unlink' && 'Remove Link'}
            {confirmAction?.type === 'swap' && 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>

      <PartyDetailDialog party={viewParty} onClose={() => setViewParty(null)} />
    </>
  );
}

// ---------------------------------------------------------------------------
// My Parties tab — the tenant's own TenantParty book.
// ---------------------------------------------------------------------------

interface MyPartiesTabProps {
  searchTerm: string;
  searchField: SearchField;
  setActionToast: (msg: string | null) => void;
  mappedPartyIds: Set<number>;
  mappingFilter: MappingFilter;
  onTotalLoaded: (total: number) => void;
}

type MappingFilter = 'all' | 'mapped' | 'unmapped';

function MyPartiesTab({ searchTerm, searchField, setActionToast, mappedPartyIds, mappingFilter, onTotalLoaded }: MyPartiesTabProps) {
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);
  const [data, setData] = useState<TenantPartyPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [viewParty, setViewParty] = useState<TenantParty | null>(null);

  // Invoice dialog
  const [invoiceDialog, setInvoiceDialog] = useState<{
    open: boolean;
    party: TenantParty | null;
    invoices: MyNetworkInvoice[] | null;
    loading: boolean;
  }>({ open: false, party: null, invoices: null, loading: false });

  const openInvoiceDialog = async (tp: TenantParty) => {
    setInvoiceDialog({ open: true, party: tp, invoices: null, loading: true });
    try {
      const all = await getNetworkInvoices(tp.id);
      setInvoiceDialog(prev => ({ ...prev, invoices: all.filter(i => (i.due ?? 0) > 0), loading: false }));
    } catch {
      setInvoiceDialog(prev => ({ ...prev, invoices: [], loading: false }));
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const fetchPage = mappingFilter !== 'all' ? 0 : page;
      const fetchSize = mappingFilter !== 'all' ? 500 : size;
      const result = await listTenantParties(searchTerm || undefined, false, fetchPage, fetchSize, searchField);
      setData(result);
      onTotalLoaded(result.totalElements);
    } catch {
      setActionToast('Could not load My Network.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, searchTerm, searchField, mappingFilter]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, searchField, mappingFilter]);

  const handlePricingChange = async (tp: TenantParty, newVal: PartyPricingTier) => {
    setSavingId(tp.id);
    try {
      await updateTenantPartyPricing(tp.id, newVal);
      load();
    } catch {
      setActionToast('Could not save billing preference.');
    } finally {
      setSavingId(null);
    }
  };

  const displayContent = useMemo(() => {
    const all = data?.content ?? [];
    if (mappingFilter === 'mapped')   return all.filter(tp => mappedPartyIds.has(tp.id));
    if (mappingFilter === 'unmapped') return all.filter(tp => !mappedPartyIds.has(tp.id));
    return [...all].sort((a, b) => {
      const aM = mappedPartyIds.has(a.id) ? 0 : 1;
      const bM = mappedPartyIds.has(b.id) ? 0 : 1;
      return aM - bM;
    });
  }, [data, mappedPartyIds, mappingFilter]);

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data || displayContent.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography sx={{ color: brand.inkSoft, mb: 2 }}>
          {searchTerm
            ? `No parties match "${searchTerm}".`
            : mappingFilter === 'mapped'
            ? 'No mapped parties yet.'
            : mappingFilter === 'unmapped'
            ? 'All parties are mapped!'
            : 'No parties in your "My Network" book yet.'}
        </Typography>
        {!searchTerm && mappingFilter === 'all' && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/tenant-parties/new')}
          >
            Create your first party
          </Button>
        )}
      </Box>
    );
  }

  return (
    <>
      <TableContainer>
        <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 480 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40, fontWeight: 600 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 130 }}>Contact</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 250 }} align="right">Outstanding</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 90 }}>Billing</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 60 }} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {displayContent.map((tp: TenantParty, idx) => {
              const contactNumber = tp.mobile || tp.phone || null;
              const areaCity = [tp.area, tp.city].filter(Boolean).join(', ');
              const gstDl = [
                tp.gstin ? `GST ${tp.gstin}` : null,
                tp.dlNumber ? `DL ${tp.dlNumber}` : null,
              ].filter(Boolean).join(' • ');
              const defaultTier: PartyPricingTier =
                tp.partyType === 'WHOLESALER' ? 'PTS' : 'PTR';
              const saving = savingId === tp.id;

              return (
                <TableRow
                  key={tp.id}
                  hover
                  sx={{ '&:nth-of-type(even)': { background: '#FBFBFD' } }}
                >
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    {data.number * data.size + idx + 1}
                  </TableCell>

                  {/* Party (consolidated) */}
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                      <Typography
                        variant="body2"
                        onClick={() => setViewParty(tp)}
                        sx={{ fontWeight: 700, color: brand.violet, lineHeight: 1.25, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {tp.name}
                      </Typography>
                      {tp.partyType && <PartyTypeChip type={tp.partyType as PartyType} />}
                      {tp.partyCode && (
                        <Typography variant="caption" sx={{ color: brand.inkSoft, fontSize: 11 }}>
                          {tp.partyCode}
                        </Typography>
                      )}
                      {mappedPartyIds.has(tp.id) && (
                        <Chip
                          size="small"
                          label="Mapped"
                          sx={{
                            height: 18,
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: '0.04em',
                            background: '#DCFCE7',
                            color: '#166534',
                            borderRadius: 0.5,
                            '& .MuiChip-label': { px: 0.75 },
                          }}
                        />
                      )}
                    </Stack>
                    {areaCity && (
                      <Typography variant="caption" sx={{ color: brand.inkSoft, fontSize: 11, display: 'block' }}>
                        📍 {areaCity}
                      </Typography>
                    )}
                    {gstDl && (
                      <Typography variant="caption" sx={{ color: brand.inkSoft, fontSize: 11, display: 'block' }}>
                        {gstDl}
                      </Typography>
                    )}
                  </TableCell>

                  {/* Contact */}
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    {contactNumber ? (
                      <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600 }}>
                        📞 {contactNumber}
                      </Typography>
                    ) : (
                      <Typography variant="caption" sx={{ color: brand.inkSoft, fontSize: 11 }}>—</Typography>
                    )}
                  </TableCell>

                  {/* Outstanding */}
                  <TableCell sx={{ verticalAlign: 'top' }} align="right">
                    {tp.outstanding != null && tp.outstanding > 0 ? (
                      <Stack spacing={0.5} alignItems="flex-end">
                        <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 700, color: '#dc2626' }}>
                          ₹{Number(tp.outstanding).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </Typography>
                        <ViewButton onClick={() => openInvoiceDialog(tp)} />
                      </Stack>
                    ) : (
                      <Typography variant="caption" sx={{ fontSize: 11, color: brand.inkSoft }}>—</Typography>
                    )}
                  </TableCell>

                  {/* Billing */}
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <RadioGroup
                      value={tp.defaultPricing ?? defaultTier}
                      onChange={(e) => handlePricingChange(tp, e.target.value as PartyPricingTier)}
                    >
                      <FormControlLabel
                        value="PTR"
                        control={<Radio size="small" disabled={saving} sx={{ p: 0.25 }} />}
                        label={<Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600 }}>PTR</Typography>}
                        sx={{ m: 0, gap: 0.5 }}
                      />
                      <FormControlLabel
                        value="PTS"
                        control={<Radio size="small" disabled={saving} sx={{ p: 0.25 }} />}
                        label={<Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600 }}>PTS</Typography>}
                        sx={{ m: 0, gap: 0.5 }}
                      />
                    </RadioGroup>
                  </TableCell>

                  {/* Actions */}
                  <TableCell sx={{ verticalAlign: 'top' }} align="right">
                    <IconButton
                      size="small"
                      title="Edit"
                      onClick={() => navigate(`/tenant-parties/${tp.id}/edit`)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {mappingFilter === 'all' && (
        <PaginationFooter
          page={data.number}
          totalPages={data.totalPages}
          total={data.totalElements}
          rowsPerPage={data.size}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={setPage}
          onRowsPerPageChange={setSize}
          label="parties"
        />
      )}

      <TenantPartyDetailDialog party={viewParty} onClose={() => setViewParty(null)} />

      {/* Outstanding Invoices Dialog */}
      <Dialog
        open={invoiceDialog.open}
        onClose={() => setInvoiceDialog(prev => ({ ...prev, open: false }))}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Outstanding Invoices</Typography>
            {invoiceDialog.party && (
              <Typography sx={{ fontSize: 12, color: brand.inkSoft, mt: 0.25 }}>
                {invoiceDialog.party.name}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={() => setInvoiceDialog(prev => ({ ...prev, open: false }))}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 0 }}>
          {invoiceDialog.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : !invoiceDialog.invoices || invoiceDialog.invoices.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ color: brand.inkSoft, fontSize: 13 }}>No pending invoices.</Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: '#f9fafb' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Invoice No</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Amount</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Paid</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#dc2626' }} align="right">Due</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoiceDialog.invoices.map(inv => (
                  <TableRow key={inv.id ?? inv.invoiceNumber} hover>
                    <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{inv.invoiceNumber}</TableCell>
                    <TableCell sx={{ fontSize: 11, color: brand.inkSoft }}>{inv.invoiceDate ?? '—'}</TableCell>
                    <TableCell sx={{ fontSize: 12 }} align="right">
                      {inv.invoiceAmount != null
                        ? `₹${Number(inv.invoiceAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
                        : '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }} align="right">
                      {inv.paid != null
                        ? `₹${Number(inv.paid).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
                        : '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, fontWeight: 700, color: '#dc2626' }} align="right">
                      ₹{Number(inv.due ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ background: '#fef2f2' }}>
                  <TableCell colSpan={4} sx={{ fontWeight: 700, fontSize: 12, color: '#dc2626' }}>
                    Total Outstanding
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 13, color: '#dc2626' }} align="right">
                    ₹{invoiceDialog.invoices
                      .reduce((s, i) => s + (i.due ?? 0), 0)
                      .toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Retailer Supplier Discovery — "New Suppliers" + "Near Me" tabs for RETAILER users.
// ---------------------------------------------------------------------------


function RetailerSuppliersView() {
  const [tab, setTab] = useState(0); // 0=All, 1=New Suppliers, 2=Near Me
  const [viewParty, setViewParty] = useState<PeerTenant | null>(null);
  const [suppliers, setSuppliers] = useState<PeerTenant[]>([]);
  const [suppliersTotal, setSuppliersTotal] = useState(0);
  const [userCity, setUserCity] = useState('');
  const [userArea, setUserArea] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchField, setSearchField] = useState<SearchField>('name');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [appliedSearchField, setAppliedSearchField] = useState<SearchField>('name');
  const handleSearch = () => {
    setAppliedSearch(search.trim());
    setAppliedSearchField(searchField);
  };
  const [allPage, setAllPage] = useState(0);
  const [newPage, setNewPage] = useState(0);
  const rowsPerPage = 25;

  useEffect(() => {
    tenantApi.getMe().then(me => {
      setUserCity(me.city ?? '');
      setUserArea(me.area ?? '');
    }).catch(() => {});
  }, []);

  // All/New/Near filtering and the search box are all applied server-side, so they
  // cover every matching row, not just whatever page happens to already be loaded.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const page = tab === 0 ? allPage : tab === 1 ? newPage : 0;
    tenantApi.listPeers(appliedSearch || undefined, page, tab === 2 ? 500 : rowsPerPage, 'WHOLESALER', {
      isNew: tab === 1, near: tab === 2, searchField: appliedSearchField,
    }).then(res => {
      if (cancelled) return;
      setSuppliers(res.content);
      setSuppliersTotal(res.totalElements);
      setLoading(false);
    }).catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tab, appliedSearch, appliedSearchField, allPage, newPage]);

  const isNew = (s: PeerTenant) => {
    if (!s.createdAt) return false;
    return Date.now() - new Date(s.createdAt).getTime() <= 30 * 86_400_000;
  };

  // `suppliers` is already filtered + paginated server-side for the active tab.
  const sorted = useMemo(() => [...suppliers].sort((a, b) => b.id - a.id), [suppliers]);

  const matchArea = (s: PeerTenant) =>
    !!userArea && s.area?.toLowerCase() === userArea.toLowerCase();

  // Near Me: area-first section, then city-only section (the underlying rows are
  // already near-filtered server-side; this just splits them into two sections).
  const areaMatches = useMemo(() => sorted.filter(matchArea), [sorted, userArea]); // eslint-disable-line react-hooks/exhaustive-deps

  const cityOnlyMatches = useMemo(() =>
    sorted.filter(s =>
      !!userCity &&
      s.city?.toLowerCase() === userCity.toLowerCase() &&
      !matchArea(s)
    ),
    [sorted, userArea, userCity]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasLocation = !!userCity || !!userArea;

  const activePage = tab === 0 ? allPage : newPage;
  const setActivePage = tab === 0 ? setAllPage : setNewPage;
  const pageRows = sorted;

  const thCellSx = { fontWeight: 700, fontSize: 11, letterSpacing: '0.06em', color: '#6b7280', bgcolor: '#f8f9fc', textTransform: 'uppercase' as const };
  const sectionRowSx = { bgcolor: '#f3f4f8' };

  function SupplierRow({ s, idx }: { s: PeerTenant; idx: number }) {
    const location = [s.area, s.city, s.district, s.state].filter(Boolean).join(', ');
    const gstDl = [s.gstin && `GST: ${s.gstin}`, s.drugLicenseNumber && `DL: ${s.drugLicenseNumber}`].filter(Boolean).join(' · ');
    return (
      <TableRow hover sx={{ '&:nth-of-type(even)': { bgcolor: '#fbfbfd' } }}>
        <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{idx}</TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25 }}>
            <Typography
              variant="body2"
              onClick={() => setViewParty(s)}
              sx={{ fontWeight: 700, color: brand.violet, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            >
              {s.name}
            </Typography>
            {isNew(s) && (
              <Chip label="NEW" size="small" sx={{ height: 16, fontSize: 9, fontWeight: 800, bgcolor: '#3DD68C', color: '#0E3520', '& .MuiChip-label': { px: 0.5 } }} />
            )}
          </Stack>
          {location && <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11, display: 'block' }}>📍 {location}</Typography>}
          {gstDl && <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11, display: 'block' }}>{gstDl}</Typography>}
        </TableCell>
        <TableCell>
          {s.phone ? (
            <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600 }}>📞 {s.phone}</Typography>
          ) : <Typography variant="caption" sx={{ color: 'text.secondary' }}>—</Typography>}
        </TableCell>
      </TableRow>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Typography variant="h2" sx={{ color: brand.navy }}>Suppliers</Typography>
        <SearchWithFieldSelect
          field={searchField}
          onFieldChange={setSearchField}
          value={search}
          onChangeText={setSearch}
          onSearch={() => { handleSearch(); setActivePage(0); }}
        />
      </Stack>

      <Tabs value={tab} onChange={(_, v: number) => { setTab(v); setAllPage(0); setNewPage(0); setSearch(''); setAppliedSearch(''); }}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { fontWeight: 600, fontSize: 13 } }}>
        <Tab label={tab === 0 ? `All Suppliers (${suppliersTotal})` : 'All Suppliers'} />
        <Tab label="New Suppliers" />
        <Tab label="Near Me" />
      </Tabs>

      {tab === 2 && !hasLocation && !loading && (
        <Alert severity="info" sx={{ mb: 2 }}>Update your area and city in your Profile to see nearby suppliers.</Alert>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : tab === 0 || tab === 1 ? (
            /* ─── All Suppliers / New Suppliers tabs (paginated) ─── */
            <>
              <TableContainer>
                <Table size="small" sx={{ minWidth: 400 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...thCellSx, width: 40 }}>#</TableCell>
                      <TableCell sx={thCellSx}>Supplier</TableCell>
                      <TableCell sx={thCellSx}>Phone</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pageRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} sx={{ textAlign: 'center', py: 4, color: 'text.secondary', fontSize: 13 }}>
                          {tab === 1 ? 'No new suppliers registered in the last 30 days.' : 'No suppliers found.'}
                        </TableCell>
                      </TableRow>
                    ) : pageRows.map((s, i) => (
                      <SupplierRow key={s.id} s={s} idx={activePage * rowsPerPage + i + 1} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <PaginationFooter
                page={activePage}
                totalPages={Math.max(1, Math.ceil(suppliersTotal / rowsPerPage))}
                total={suppliersTotal}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[rowsPerPage]}
                onPageChange={setActivePage}
                onRowsPerPageChange={() => {}}
                label="suppliers"
              />
            </>
          ) : (
            /* ─── Near Me tab (area first, then city) ─── */
            <TableContainer>
              <Table size="small" sx={{ minWidth: 400 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...thCellSx, width: 40 }}>#</TableCell>
                    <TableCell sx={thCellSx}>Supplier</TableCell>
                    <TableCell sx={thCellSx}>Phone</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {areaMatches.length === 0 && cityOnlyMatches.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ textAlign: 'center', py: 4, color: 'text.secondary', fontSize: 13 }}>
                        {!hasLocation
                          ? 'Update your area and city in Profile.'
                          : userArea
                            ? `No suppliers found in ${userArea} or ${userCity}.`
                            : `No suppliers found in ${userCity}.`}
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {areaMatches.length > 0 && (
                        <>
                          <TableRow sx={sectionRowSx}>
                            <TableCell colSpan={3} sx={{ py: 0.75, px: 2, fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                              In Your Area — {userArea}
                            </TableCell>
                          </TableRow>
                          {areaMatches.map((s, i) => <SupplierRow key={s.id} s={s} idx={i + 1} />)}
                        </>
                      )}
                      {cityOnlyMatches.length > 0 && (
                        <>
                          <TableRow sx={sectionRowSx}>
                            <TableCell colSpan={3} sx={{ py: 0.75, px: 2, fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                              In {userCity}
                            </TableCell>
                          </TableRow>
                          {cityOnlyMatches.map((s, i) => <SupplierRow key={s.id} s={s} idx={i + 1} />)}
                        </>
                      )}
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {!loading && (tab === 0 || tab === 1) && suppliersTotal > rowsPerPage && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, py: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {activePage * rowsPerPage + 1}–{Math.min((activePage + 1) * rowsPerPage, suppliersTotal)} of {suppliersTotal}
                </Typography>
                <Button size="small" disabled={activePage === 0} onClick={() => setActivePage(p => p - 1)}>Prev</Button>
                <Button size="small" disabled={(activePage + 1) * rowsPerPage >= suppliersTotal} onClick={() => setActivePage(p => p + 1)}>Next</Button>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      <PartyDetailDialog party={viewParty} onClose={() => setViewParty(null)} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Top-level page — wraps the tabs and shared header.
// ---------------------------------------------------------------------------

type TabKey = 'all' | 'my';

export function PartiesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const locationState = location.state as LocationState | undefined;
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get('tab');
  const tab: TabKey = tabParam === 'my' ? 'my' : 'all';
  const setTab = (next: TabKey) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'all') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);
  const [data, setData] = useState<PeerTenantPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // All / My Network each own a fully independent search query + field —
  // switching tabs must never carry one tab's field selection into the other.
  const [allSearchInput, setAllSearchInput] = useState('');
  const [allSearchField, setAllSearchField] = useState<SearchField>('name');
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedAllSearchField, setAppliedAllSearchField] = useState<SearchField>('name');

  const [mySearchInput, setMySearchInput] = useState('');
  const [mySearchField, setMySearchField] = useState<SearchField>('name');
  const [myAppliedQuery, setMyAppliedQuery] = useState('');
  const [myAppliedField, setMyAppliedField] = useState<SearchField>('name');

  const [createdToast, setCreatedToast] = useState(false);
  const [actionToast, setActionToast] = useState<string | null>(null);

  const [mappingFilter, setMappingFilter] = useState<MappingFilter>('all');
  const [myPartiesTotalCount, setMyPartiesTotalCount] = useState(0);

  // Reset filter when leaving My Parties tab
  useEffect(() => {
    if (tab !== 'my') setMappingFilter('all');
  }, [tab]);

  // ── Auto-map by GST ──────────────────────────────────────────────────────
  const [mappedPartyIds, setMappedPartyIds] = useState<Set<number>>(new Set());
  const [mapping, setMapping] = useState(false);
  const [mapResult, setMapResult] = useState<{
    mapped: number;
    alreadyMapped: number;
    noMatch: number;
  } | null>(null);

  useEffect(() => {
    if (locationState?.created) {
      setCreatedToast(true);
      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAllSearch = () => {
    setSearchTerm(allSearchInput.trim());
    setAppliedAllSearchField(allSearchField);
    setPage(0);
  };

  const handleMySearch = () => {
    setMyAppliedQuery(mySearchInput.trim());
    setMyAppliedField(mySearchField);
  };

  const loadAllParties = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await tenantApi.listPeers(searchTerm || undefined, page, size, undefined, { searchField: appliedAllSearchField });
      if (user?.tenantType === 'RETAILER') {
        result.content = result.content.filter(t => t.tenantType !== 'RETAILER');
      }
      setData(result);
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      setError(e?.message || 'Could not load parties.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab !== 'all') return;
    loadAllParties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, searchTerm, appliedAllSearchField, tab]);

  // Load which My-Party IDs are already mapped to a peer (for the Mapped chip)
  const loadMappedPartyIds = async () => {
    try {
      // Fetch up to 500 peers — enough for most tenants
      const result = await tenantApi.listPeers(undefined, 0, 500);
      const ids = new Set<number>();
      for (const p of result.content) {
        const linkedId = p.linkedTenantParty?.id;
        if (linkedId) ids.add(linkedId);
      }
      setMappedPartyIds(ids);
    } catch { /* ignore — chips just won't show */ }
  };

  // Trigger when switching to the My Parties tab
  useEffect(() => {
    if (tab === 'my') loadMappedPartyIds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Auto-map My Parties to All Parties peers by matching GSTIN
  const handleMapMyParties = async () => {
    setMapping(true);
    setMapResult(null);
    try {
      const pageSize = 100;

      // 1. Fetch all My Parties (paginated)
      const myParties: TenantParty[] = [];
      let pg = 0;
      while (true) {
        const res = await listTenantParties(undefined, false, pg, pageSize);
        myParties.push(...res.content);
        if (res.content.length < pageSize || myParties.length >= res.totalElements) break;
        pg++;
      }

      // 2. Fetch all Peer Tenants (paginated)
      const peers: PeerTenant[] = [];
      pg = 0;
      while (true) {
        const res = await tenantApi.listPeers(undefined, pg, pageSize);
        peers.push(...res.content);
        if (res.content.length < pageSize || peers.length >= res.totalElements) break;
        pg++;
      }

      // 3. Build peer lookup by normalised GSTIN
      const peerByGst = new Map<string, PeerTenant>();
      for (const p of peers) {
        const gst = p.gstin?.trim().toUpperCase();
        if (gst) peerByGst.set(gst, p);
      }

      // 4. Match and link
      let mapped = 0, alreadyMapped = 0, noMatch = 0;
      for (const tp of myParties) {
        const gst = tp.gstin?.trim().toUpperCase();
        if (!gst) { noMatch++; continue; }
        const peer = peerByGst.get(gst);
        if (!peer) { noMatch++; continue; }
        if (peer.linkedTenantParty?.id === tp.id) { alreadyMapped++; continue; }
        try {
          await linkPeerToTenantParty(tp.id, peer.id);
          mapped++;
        } catch { noMatch++; }
      }

      setMapResult({ mapped, alreadyMapped, noMatch });
      // Refresh mapped IDs so chips appear immediately
      await loadMappedPartyIds();
    } catch {
      setActionToast('Auto-mapping failed. Please try again.');
    } finally {
      setMapping(false);
    }
  };

  const totalText = useMemo(() => {
    return tab === 'all' && data ? `${data.totalElements} total` : '';
  }, [data, tab]);

  if (user?.tenantType === 'RETAILER') {
    return <RetailerSuppliersView />;
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Typography variant="h2" sx={{ color: brand.navy, mb: 0.5 }}>
            {user?.tenantType === 'WHOLESALER' ? 'Network' : 'Parties'}
          </Typography>
          <Typography variant="body2" sx={{ color: brand.inkSoft }}>
            {tab === 'all'
              ? `Other registered businesses in your industry. ${totalText}`
              : 'Your "My Network" book.'}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          {tab === 'all' ? (
            <SearchWithFieldSelect
              field={allSearchField}
              onFieldChange={setAllSearchField}
              value={allSearchInput}
              onChangeText={setAllSearchInput}
              onSearch={handleAllSearch}
            />
          ) : (
            <SearchWithFieldSelect
              field={mySearchField}
              onFieldChange={setMySearchField}
              value={mySearchInput}
              onChangeText={setMySearchInput}
              onSearch={handleMySearch}
            />
          )}
          <IconButton
            onClick={() => { if (tab === 'all') loadAllParties(); }}
            aria-label="refresh"
          >
            <RefreshIcon />
          </IconButton>

          {tab === 'my' && (
            <>
              <Button
                variant="outlined"
                startIcon={
                  mapping
                    ? <CircularProgress size={14} color="inherit" />
                    : <LinkIcon />
                }
                onClick={handleMapMyParties}
                disabled={mapping}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Auto-Map by GST
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/tenant-parties/new')}
              >
                Create Party
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      <Box sx={{ display: 'flex', alignItems: 'flex-end', borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v: TabKey) => setTab(v)}
          sx={{
            minHeight: 40,
            '& .MuiTab-root': {
              textTransform: 'uppercase',
              fontWeight: 600,
              letterSpacing: '0.04em',
              fontSize: 13,
              minHeight: 40,
              py: 1,
            },
          }}
        >
          <Tab value="all" label="All" />
          <Tab value="my" label="My Network" />
        </Tabs>
        {tab === 'my' && (
          <Stack direction="row" spacing={0.5} sx={{ ml: 'auto', pb: 0.75, pr: 0.5 }}>
            {(['all', 'mapped', 'unmapped'] as MappingFilter[]).map(f => {
              const mappedCount = mappedPartyIds.size;
              const unmappedCount = Math.max(0, myPartiesTotalCount - mappedCount);
              const label =
                f === 'all' ? `All (${myPartiesTotalCount})`
                : f === 'mapped' ? `Mapped (${mappedCount})`
                : `Unmapped (${unmappedCount})`;
              return (
                <Chip
                  key={f}
                  label={label}
                  onClick={() => setMappingFilter(f)}
                  variant={mappingFilter === f ? 'filled' : 'outlined'}
                  size="small"
                  sx={mappingFilter === f ? {
                    background: brand.violet, color: '#fff', fontWeight: 700,
                    '&:hover': { background: brand.violet },
                  } : { fontWeight: 600 }}
                />
              );
            })}
          </Stack>
        )}
      </Box>

      {error && tab === 'all' && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {mapResult && tab === 'my' && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMapResult(null)}>
          Auto-map complete — <strong>{mapResult.mapped}</strong> newly mapped,{' '}
          <strong>{mapResult.alreadyMapped}</strong> already mapped,{' '}
          <strong>{mapResult.noMatch}</strong> without a GST match.
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          {tab === 'all' ? (
            <AllPartiesTab
              data={data}
              loading={loading}
              searchTerm={searchTerm}
              setPage={setPage}
              setSize={setSize}
              load={loadAllParties}
              setActionToast={setActionToast}
            />
          ) : (
            <MyPartiesTab
              searchTerm={myAppliedQuery}
              searchField={myAppliedField}
              setActionToast={setActionToast}
              mappedPartyIds={mappedPartyIds}
              mappingFilter={mappingFilter}
              onTotalLoaded={setMyPartiesTotalCount}
            />
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={createdToast}
        autoHideDuration={3500}
        onClose={() => setCreatedToast(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setCreatedToast(false)}>
          Party saved successfully.
        </Alert>
      </Snackbar>

      <Snackbar
        open={actionToast !== null}
        autoHideDuration={4000}
        onClose={() => setActionToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={() => setActionToast(null)}>
          {actionToast}
        </Alert>
      </Snackbar>
    </Box>
  );
}
